import { OpenPanelComponent } from "@openpanel/nextjs";
// import * as Sentry from "@sentry/nextjs";
import { Hydrate, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import ThemeProvider from "../components/ThemeProvider";
import { AppWrapper } from "../contexts/StoreDataContext";
import { getStoreClientSide } from "../services/frontend/client-apis";
// import { nodeEnv } from "../services/helper";
import { registerSuperProperties, trackPageView } from "../utils/analytics";
import { installApiTracing } from "../utils/apiTrace";
import { installClientLogCapture } from "../utils/clientLogs";
import { initPostHog } from "../utils/posthogClient";
import { buildStoreSuperProperties } from "../utils/storeSuperProperties";
import { clearLastTraceId } from "../utils/trace";
import { initializeTracking } from "../utils/tracking";
import "../styles/globals.css";
const analyticsConfig = require("../utils/analyticsConfig"); // Import config

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [themeData, setThemeData] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [error, setError] = useState(null);
  const [storeUrl, setStoreUrl] = useState(null);
  const [isProplanActive, setIsProplanActive] = useState(false);

  // The initial $pageview must not ship before registerSuperProperties has
  // set store_id below, or the very first pageview of every session (used for
  // per-store landing-page funnels) is missing it. Fired once, at whichever
  // of "PostHog started" / "store fetch settled" happens last.
  const posthogReadyRef = useRef(false);
  const storeFetchSettledRef = useRef(false);
  const initialPageviewFiredRef = useRef(false);
  const maybeFireInitialPageview = () => {
    if (initialPageviewFiredRef.current) return;
    if (!posthogReadyRef.current || !storeFetchSettledRef.current) return;
    initialPageviewFiredRef.current = true;
    trackPageView(window.location.pathname);
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: 1,
            // Chrome can report navigator.onLine === false while the network is fine; the
            // default networkMode would then pause every query and leave the store on
            // skeletons forever. We have no offline UX, so always attempt the request.
            networkMode: "always",
          },
        },
      }),
  );

  useEffect(() => {
    const fetchStoreAndTheme = async () => {
      try {
        const response = await getStoreClientSide();

        // FIXME: Remove this after testing
        // response.storeData.proplan.isActive = false;
        const proplanActive = response.storeData?.proplan?.isActive ?? false;
        setIsProplanActive(proplanActive);

        // Registered here, synchronously with the fetch that resolves it,
        // rather than in a separate effect keyed on storeData: that effect
        // runs after this component's children have already committed their
        // own mount effects (e.g. the STORE_VIEWED/PRODUCT_VIEWED trackEvent
        // calls), so those events would ship without store_id.
        registerSuperProperties(buildStoreSuperProperties(response.storeData));

        setStoreData(response.storeData);
        setThemeData(response.themeData);
        setError(response.error);
        setStoreUrl(response.storeUrl);
      } catch (err) {
        console.error("🚀 ~ fetchStoreAndTheme ~ err:", err);
      } finally {
        storeFetchSettledRef.current = true;
        maybeFireInitialPageview();
      }
    };

    fetchStoreAndTheme();
  }, []);

  // useEffect(() => {
  //   // Initialize Sentry only on the client side
  //   if (typeof window !== "undefined") {
  //     Sentry.init({
  //       dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || process.env.SENTRY_DSN,
  //       tracesSampleRate: 0,
  //       environment: nodeEnv,
  //       enabled: nodeEnv !== "development",
  //       debug: false,
  //     });
  //   }
  // }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (typeof window !== "undefined") {
      (async () => {
        const { BrowserAgent } = await import("@newrelic/browser-agent/loaders/browser-agent");
        const options = {
          info: {
            applicationID: 653444045,
            beacon: "bam.nr-data.net",
            errorBeacon: "bam.nr-data.net",
            licenseKey: "NRJS-afa44d47157ac6eb0b9",
            sa: 1,
          },
          init: {
            ajax: {
              deny_list: ["bam.nr-data.net"],
            },
            browser_consent_mode: {
              enabled: false,
            },
            distributed_tracing: {
              enabled: true,
            },
            performance: {
              capture_detail: false,
              capture_marks: false,
              capture_measures: true,
            },
            privacy: {
              cookies_enabled: true,
            },
          },
          loader_config: {
            accountID: 8478586,
            agentID: 653444045,
            applicationID: 653444045,
            licenseKey: "NRJS-afa44d47157ac6eb0b9",
            trustKey: 8478586,
          },
        };
        new BrowserAgent(options);
      })();
    }
  }, []);

  // PostHog: start the SDK, forward console warnings/errors, and record the
  // first page view. Everything here no-ops until NEXT_PUBLIC_POSTHOG_KEY is set.
  useEffect(() => {
    if (!initPostHog()) return;
    installClientLogCapture();
    // Behind the same guard on purpose: with no PostHog there is nothing to
    // correlate a trace id *to*, so window.fetch is left entirely unwrapped
    // rather than carrying a patch that can only cost something.
    installApiTracing();
    posthogReadyRef.current = true;
    maybeFireInitialPageview();
  }, []);

  // Page views for client-side route changes. The SDK cannot see these itself
  // because the pages router swaps components without a document load.
  useEffect(() => {
    const handleRouteChange = (url) => {
      // The previous page's trace must not survive the navigation, or an
      // error on the new page gets correlated to a stale trace.
      clearLastTraceId();
      trackPageView(url);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);

  // Initialize tracking on app load
  useEffect(() => {
    // Don't re-capture tracking data on checkout page to preserve UTM parameters
    if (typeof window !== "undefined" && window.location.pathname !== "/cart/checkout") {
      initializeTracking();
    }
  }, []);

  // Determine if OpenPanel should track page views automatically
  const shouldOpenPanelTrackPageViews = analyticsConfig.pageViewProviders.includes("openpanel");

  // DISABLED 2026-09-03 — GTM + Facebook Pixel inits. See TRACKING.md.
  // const GTM_ID = "GTM-NSVMFWJ9";
  //console.log("pageProps?.storeData", pageProps?.storeData);

  return (
    <AppWrapper sharedState={{ storeData, themeData, error, storeUrl, isProplanActive }}>
      <Head>
        {/* DISABLED 2026-09-03 — Google Tag Manager (GTM-NSVMFWJ9).
            Loading gtm.js emits to Google. Remaining dataLayer.push call sites
            no-op when window.dataLayer is undefined.
            Re-enable: uncomment GTM_ID above and this <script>. */}
        {/* <script
          id="gtm"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${GTM_ID}');
  `,
          }}
        /> */}

        {/* DISABLED 2026-09-03 — Facebook Pixel.
            Re-enable: uncomment this block. Purchase tracking in
            pages/order/[orderId].js is also commented; uncomment both. */}
        {/* {isProplanActive && storeData?.facebookPixel && (
          <>
            <script
              id="fb"
              dangerouslySetInnerHTML={{
                __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${storeData.facebookPixel}');
            fbq('track', 'PageView');`,
              }}
            />
            <noscript
              dangerouslySetInnerHTML={{
                __html: `
            <img height="1" width="1" style="display:none"
            src="https://www.facebook.com/tr?id=${storeData.facebookPixel}&ev=PageView&noscript=1"/>`,
              }}
            />
          </>
        )} */}

        {/* preconnect hints */}
        {process.env.NEXT_PUBLIC_CDN_HOST ? (
          <link
            rel="preconnect"
            href={`https://${process.env.NEXT_PUBLIC_CDN_HOST.replace(/^https?:\/\//, "")}`}
          />
        ) : null}
        <link rel="preconnect" href="https://dm2buy-aqbqh9cwb5cwb9he.z02.azurefd.net" />
      </Head>

      {
        /* OpenPanel initialization */
        analyticsConfig.defaultProviders.includes("openpanel") && (
          <OpenPanelComponent
            clientId={
              process.env.NEXT_PUBLIC_OPENPANEL_CLIENT_ID || "0a55c612-c2cc-40a9-bcb7-54ea151d1939"
            }
            trackScreenViews={shouldOpenPanelTrackPageViews}
            trackOutgoingLinks={true}
            profileId={pageProps?.userData?.id}
            // apiUrl="/api/op"
            apiUrl="https://openpanel.dm2buy.com/api"
            filter={`
          function filter(event) {
            console.log("OpenPanel event filter:", event);
            return true; // Allow all events
          }
        `}
            waitForProfile={false}
          />
        )
      }
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <ThemeProvider themeData={themeData} isProplanActive={isProplanActive}>
            <Component {...pageProps} />
          </ThemeProvider>
        </Hydrate>
      </QueryClientProvider>
    </AppWrapper>
  );
}

export default MyApp;
